import {
  parseEntityReference,
  stripMeetingIntentPrompt,
  stripRoutinePreamble,
  type EntityReference,
} from '@littlebird/markdown-utils';
import { Marked, type TokensList } from 'marked';

export type EntityReferenceToken = EntityReference & {
  type: 'entityReference';
};

const PARSE_CACHE_LIMIT = 200;
const parseCache = new Map<string, TokensList>();
const MATH_OPERATOR_TOKEN_REGEX = /[=+*/^_{}\[\]<>]|(?:^|\s)-|-(?:$|\s)/;
const TEX_COMMAND_REGEX = /\\[A-Za-z]+/;
const TEX_SYMBOL_ESCAPE_REGEX = /\\[^$\s]/;
// Product rule: explicit `$...$` with no whitespace is math syntax.
// Prose/currency/non-TeX exceptions stay literal.
const NO_WHITESPACE_MATH_REGEX = /^\S+$/;
const URL_DOLLAR_SPAN_REGEX = /^[a-z][a-z0-9+.-]*:\/\/\S+$/i;
const SEMVER_DOLLAR_SPAN_REGEX =
  /^v?\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?(?:\+[0-9A-Za-z.-]+)?$/;
const PATH_DOLLAR_SPAN_REGEX =
  /^(?:\.{1,2}|~)\/\S+$|^(?:[\w.-]+\/)+[\w.-]+\.[A-Za-z0-9]+$|^[A-Za-z]:[\\/]\S+$/;
const COMPACT_IDENTIFIER_LIST_REGEX =
  /^[A-Za-z]{1,2}(?:\s*,\s*[A-Za-z]{1,2})+$/;
const ABSOLUTE_VALUE_REGEX = /\|[^|\n]+\|/;
const BLOCK_MATH_START_REGEX = /(^|\n) {0,3}\$\$/;
const APPROXIMATE_DOLLAR_PREFIX_REGEX = /^~\$\s*(?=\d)/;
const APPROXIMATE_NUMBER_PREFIX_REGEX = /^~(?=[+-]?\d)/;
const SINGLE_TILDE_DEL_FOLLOWERS = '.,;:!?()[]{}\'"`/-–—';
const CURRENCY_AMOUNT_REGEX_SOURCE =
  '(?:\\d{1,3}(?:,\\d{3})+(?:\\.\\d+)?|\\d+(?:[.,]\\d+)?)';
const COMPACT_GROUPED_MATH_SUFFIX_REGEX = new RegExp(
  `^\\s*\\((?:[A-Za-z][A-Za-z0-9]*|${CURRENCY_AMOUNT_REGEX_SOURCE})\\)\\s*$`
);
const CURRENCY_UNIT_REGEX_SOURCE =
  '(?:[A-Za-z][A-Za-z0-9]*|\\d+[A-Za-z][A-Za-z0-9]*)';
const CURRENCY_PROSE_BOUNDARY_REGEX_SOURCE =
  '(?:\\s+[A-Za-z(]|\\s*[.,;:!?]\\s*[A-Za-z(])';
const CURRENCY_BRACKETED_PROSE_BOUNDARY_REGEX_SOURCE =
  '\\s*[\\])}\'"]+\\s*(?:[.,;:!?])?\\s*(?:[A-Za-z(]|$)';
const CURRENCY_RANGE_PREFIX_REGEX = new RegExp(
  `^\\s*${CURRENCY_AMOUNT_REGEX_SOURCE}\\s*[-–—]\\s*$`
);
const CURRENCY_RANGE_PROSE_REGEX = new RegExp(
  `^\\s*${CURRENCY_AMOUNT_REGEX_SOURCE}\\s*[-–—]\\s*${CURRENCY_AMOUNT_REGEX_SOURCE}(?=${CURRENCY_PROSE_BOUNDARY_REGEX_SOURCE}|\\s*[.,;:!?]\\s*$)`
);
const CURRENCY_AMOUNT_UNIT_PREFIX_REGEX = new RegExp(
  `^\\s*${CURRENCY_AMOUNT_REGEX_SOURCE}\\s*\\/\\s*${CURRENCY_UNIT_REGEX_SOURCE}`
);
const CURRENCY_RANGE_BRACKETED_PROSE_REGEX = new RegExp(
  `^\\s*${CURRENCY_AMOUNT_REGEX_SOURCE}\\s*[-–—]\\s*${CURRENCY_AMOUNT_REGEX_SOURCE}(?=${CURRENCY_BRACKETED_PROSE_BOUNDARY_REGEX_SOURCE})`
);
const CURRENCY_UNIT_PROSE_REGEX = new RegExp(
  `^\\s*${CURRENCY_AMOUNT_REGEX_SOURCE}\\s*\\/(?:\\s*${CURRENCY_UNIT_REGEX_SOURCE}\\s*\\/)*\\s*${CURRENCY_UNIT_REGEX_SOURCE}(?=${CURRENCY_PROSE_BOUNDARY_REGEX_SOURCE})`
);
const CURRENCY_UNIT_BRACKETED_PROSE_REGEX = new RegExp(
  `^\\s*${CURRENCY_AMOUNT_REGEX_SOURCE}\\s*\\/(?:\\s*${CURRENCY_UNIT_REGEX_SOURCE}\\s*\\/)*\\s*${CURRENCY_UNIT_REGEX_SOURCE}(?=${CURRENCY_BRACKETED_PROSE_BOUNDARY_REGEX_SOURCE})`
);

interface ParseMarkdownOptions {
  breaks?: boolean;
}

function hasExplicitMathSignal(candidate: string): boolean {
  return (
    MATH_OPERATOR_TOKEN_REGEX.test(candidate) ||
    TEX_COMMAND_REGEX.test(candidate) ||
    TEX_SYMBOL_ESCAPE_REGEX.test(candidate) ||
    ABSOLUTE_VALUE_REGEX.test(candidate)
  );
}

function isLikelyMathSpan(candidate: string): boolean {
  const trimmedCandidate = candidate.trim();
  if (
    !trimmedCandidate ||
    candidate.includes('`') ||
    hasUnescapedDollar(candidate) ||
    isNonTexDollarSpan(trimmedCandidate)
  ) {
    return false;
  }

  return (
    hasExplicitMathSignal(trimmedCandidate) ||
    NO_WHITESPACE_MATH_REGEX.test(trimmedCandidate) ||
    COMPACT_IDENTIFIER_LIST_REGEX.test(trimmedCandidate)
  );
}

function isNonTexDollarSpan(candidate: string): boolean {
  return (
    URL_DOLLAR_SPAN_REGEX.test(candidate) ||
    SEMVER_DOLLAR_SPAN_REGEX.test(candidate) ||
    PATH_DOLLAR_SPAN_REGEX.test(candidate)
  );
}

function hasUnescapedDollar(candidate: string): boolean {
  return hasUnescapedCharacter(candidate, '$');
}

function hasUnescapedCharacter(candidate: string, target: string): boolean {
  let trailingBackslashCount = 0;

  for (const character of candidate) {
    if (character === target && trailingBackslashCount % 2 === 0) {
      return true;
    }

    trailingBackslashCount =
      character === '\\' ? trailingBackslashCount + 1 : 0;
  }

  return false;
}

function isCurrencyMathCollision(
  candidate: string,
  closingTail: string
): boolean {
  const closingStartsCurrencyUnit = /^[\/\d]/.test(closingTail.trimStart());
  const currencyRangeProseMatch = CURRENCY_RANGE_PROSE_REGEX.exec(candidate);
  const rangeSuffix =
    currencyRangeProseMatch === null
      ? ''
      : candidate.slice(currencyRangeProseMatch[0].length);
  const rangeSuffixHasMathSignal =
    hasExplicitMathSignal(rangeSuffix) ||
    COMPACT_GROUPED_MATH_SUFFIX_REGEX.test(rangeSuffix);
  const rangePrecedesCurrencyUnit =
    currencyRangeProseMatch !== null &&
    !rangeSuffixHasMathSignal &&
    CURRENCY_AMOUNT_UNIT_PREFIX_REGEX.test(closingTail);

  return (
    candidate.trimStart().startsWith('/') ||
    CURRENCY_UNIT_BRACKETED_PROSE_REGEX.test(candidate) ||
    CURRENCY_RANGE_BRACKETED_PROSE_REGEX.test(candidate) ||
    rangePrecedesCurrencyUnit ||
    (closingStartsCurrencyUnit &&
      (CURRENCY_UNIT_PROSE_REGEX.test(candidate) ||
        CURRENCY_RANGE_PREFIX_REGEX.test(candidate)))
  );
}

function hasClosedSingleTildeDel(src: string): boolean {
  if (!src.startsWith('~') || src.startsWith('~~')) return false;

  const firstContentCharacter = src[1];
  if (!firstContentCharacter || /\s/.test(firstContentCharacter)) return false;

  const closeIndex = src.indexOf('~', 1);
  if (closeIndex === -1) return false;

  const follower = src[closeIndex + 1];
  return (
    !follower ||
    /\s/.test(follower) ||
    SINGLE_TILDE_DEL_FOLLOWERS.includes(follower)
  );
}

function findClosingDollar(src: string, startIndex: number, delimiter = '$') {
  for (let index = startIndex; index < src.length; index++) {
    if (src[index] === '\n') return -1;
    if (
      src.startsWith(delimiter, index) &&
      !isEscapedDelimiter(src, index) &&
      isValidClosingDollarFollower(src[index + delimiter.length])
    ) {
      return index;
    }
  }

  return -1;
}

function findClosingBlockDollar(src: string, startIndex: number) {
  const environmentStack: string[] = [];
  const environmentRegex = /\\(begin|end)\{([^}\n]+)\}/y;
  const scannerRegex = /\\|\$\$|\n/g;
  let sawNewline = false;

  scannerRegex.lastIndex = startIndex;

  for (
    let match = scannerRegex.exec(src);
    match;
    match = scannerRegex.exec(src)
  ) {
    const index = match.index;

    if (match[0] === '\n') {
      sawNewline = true;
      continue;
    }

    if (match[0] === '\\') {
      environmentRegex.lastIndex = index;
      const environmentMatch = environmentRegex.exec(src);
      if (environmentMatch) {
        updateBlockEnvironmentStack(
          environmentStack,
          environmentMatch[1],
          environmentMatch[2]
        );
        scannerRegex.lastIndex = environmentRegex.lastIndex;
      }
      continue;
    }

    if (
      isEscapedDelimiter(src, index) ||
      !isBlockClosingDollarTail(src, index + 2)
    ) {
      scannerRegex.lastIndex = index + 1;
      continue;
    }

    if (!sawNewline) {
      if (environmentStack.length > 0) {
        scannerRegex.lastIndex = index + 1;
        continue;
      }
      return index;
    }

    if (environmentStack.length === 0) return index;

    scannerRegex.lastIndex = index + 1;
  }

  return -1;
}

function isEscapedDelimiter(src: string, index: number) {
  let backslashCount = 0;

  for (let cursor = index - 1; src[cursor] === '\\'; cursor--) {
    backslashCount++;
  }

  return backslashCount % 2 === 1;
}

function updateBlockEnvironmentStack(
  environmentStack: string[],
  direction: string,
  environmentName: string
) {
  if (direction === 'begin') {
    environmentStack.push(environmentName);
    return;
  }

  const lastIndex = environmentStack.lastIndexOf(environmentName);
  if (lastIndex !== -1) environmentStack.splice(lastIndex, 1);
}

function isBlockClosingDollarTail(src: string, startIndex: number) {
  let index = startIndex;
  while (src[index] === ' ' || src[index] === '\t') index++;
  return !src[index] || src[index] === '\n';
}

function getBlockMathRawEnd(src: string, closeIndex: number) {
  let index = closeIndex + 2;
  while (src[index] === ' ' || src[index] === '\t') index++;
  while (src[index] === '\n') index++;
  return index;
}

function isValidClosingDollarFollower(follower: string | undefined) {
  return !follower || !/[A-Za-z0-9\\]/.test(follower);
}

function createSmoothdownMarked({ breaks }: Required<ParseMarkdownOptions>) {
  return new Marked({
    gfm: true,
    breaks,
    extensions: [
      {
        name: 'entityReference',
        level: 'inline',
        start(src) {
          return src.indexOf('::');
        },
        tokenizer(src): EntityReferenceToken | undefined {
          const reference = parseEntityReference(src);
          if (!reference) return undefined;
          return { type: 'entityReference', ...reference };
        },
      },
      {
        name: 'math',
        level: 'block',
        start(src) {
          const match = BLOCK_MATH_START_REGEX.exec(src);
          if (!match) return -1;
          return match.index + match[1].length;
        },
        tokenizer(src) {
          const openerMatch = src.match(/^ {0,3}\$\$[ \t]*/);
          if (!openerMatch) return undefined;

          const contentStart = openerMatch[0].length;
          const closeIndex = findClosingBlockDollar(src, contentStart);
          if (closeIndex === -1) return undefined;

          return {
            type: 'math',
            raw: src.slice(0, getBlockMathRawEnd(src, closeIndex)),
            text: src.slice(contentStart, closeIndex).trim(),
          };
        },
      },
      {
        name: 'inlineMath',
        level: 'inline',
        start(src) {
          return src.indexOf('$');
        },
        tokenizer(src) {
          if (!src.startsWith('$')) return undefined;

          const delimiter = src.startsWith('$$') ? '$$' : '$';
          const contentStart = delimiter.length;

          const closeIndex = findClosingDollar(src, contentStart, delimiter);
          if (closeIndex === -1) return undefined;

          const raw = src.slice(0, closeIndex + delimiter.length);
          const text = src.slice(contentStart, closeIndex);
          const closingTail = src.slice(closeIndex + delimiter.length);
          if (delimiter === '$' && isNonTexDollarSpan(text.trim())) {
            return {
              type: 'text',
              raw,
              text: raw,
            };
          }
          if (delimiter === '$' && isCurrencyMathCollision(text, closingTail)) {
            return undefined;
          }
          if (!isLikelyMathSpan(text)) return undefined;

          return {
            type: 'inlineMath',
            raw,
            text,
          };
        },
      },
      {
        name: 'approximateDollarText',
        level: 'inline',
        start(src) {
          return src.indexOf('~$');
        },
        tokenizer(src) {
          if (hasClosedSingleTildeDel(src)) return undefined;

          const match = src.match(APPROXIMATE_DOLLAR_PREFIX_REGEX);
          if (!match) return undefined;

          return {
            type: 'text',
            raw: match[0],
            text: match[0],
          };
        },
      },
      {
        name: 'approximateNumberText',
        level: 'inline',
        start(src) {
          return src.indexOf('~');
        },
        tokenizer(src) {
          if (hasClosedSingleTildeDel(src)) return undefined;

          const match = src.match(APPROXIMATE_NUMBER_PREFIX_REGEX);
          if (!match) return undefined;

          return {
            type: 'text',
            raw: match[0],
            text: match[0],
          };
        },
      },
    ],
  });
}

const smoothdownMarked = createSmoothdownMarked({ breaks: false });
const smoothdownMarkedWithBreaks = createSmoothdownMarked({ breaks: true });

export function parseMarkdownIntoBlocks(
  markdown: string,
  options: ParseMarkdownOptions = {}
): TokensList {
  const cacheKey = `${options.breaks ? 'breaks' : 'default'}:${markdown}`;
  const cached = parseCache.get(cacheKey);
  if (cached) {
    parseCache.delete(cacheKey);
    parseCache.set(cacheKey, cached);
    return cached;
  }

  const parser = options.breaks ? smoothdownMarkedWithBreaks : smoothdownMarked;
  const tokens = parser.lexer(markdown);
  parseCache.set(cacheKey, tokens);

  if (parseCache.size > PARSE_CACHE_LIMIT) {
    const oldestKey = parseCache.keys().next().value;
    if (oldestKey !== undefined) parseCache.delete(oldestKey);
  }

  return tokens;
}

// Tags whose content should never reach the renderer.
export function stripHiddenTags(markdown: string | undefined | null): string {
  return stripMeetingIntentPrompt(stripRoutinePreamble(markdown ?? ''));
}

/**
 * Creates a short, low-collision hash for React keys by combining:
 * length, first 6 chars, and last 6 chars.
 * Not cryptographically secure.
 */

export function simpleHash(str: string) {
  const len = str.length;
  return `${len}-${str.slice(0, 6)}-${str.slice(-6)}`;
}
